import { RefObject, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik, FormikProps } from "formik";
import { array, object, string } from "yup";

import { SettingsArrayField } from "components/OrganizationAddToken/SettingsArrayField";
import { ApiTokenUpdateData } from "types/Api";
import { OrganizationApiToken } from "types/Organization";

export interface FormValues {
  settingsArray: Array<{
    key: string;
    value: string;
  }>;
}

interface AddSettingsFormProps {
  token: OrganizationApiToken;
  onSubmit: (values: ApiTokenUpdateData) => void;
  formRef: RefObject<FormikProps<FormValues>>;
}

export const AddSettingsForm = ({
  token,
  formRef,
  onSubmit,
}: AddSettingsFormProps): JSX.Element => {
  const { t } = useTranslation();
  const initialValues = useMemo(
    () => ({ settingsArray: [{ key: "", value: "" }] }),
    []
  );
  const validationSchema = useMemo(
    () =>
      object().shape({
        settingsArray: array().of(
          object().shape({
            key: string().required(t("Required")),
            value: string().required(t("Required")),
          })
        ),
      }),
    [t]
  );

  const onFormSubmit = useCallback(
    (values: FormValues) => {
      return onSubmit({
        id: token.id,
        name: token.name,
        description: token.description,
        permissions: token.permissions.map((permission) => permission),
        settings: {
          ...token.settings,
          ...(values.settingsArray.length === 0
            ? undefined
            : values.settingsArray.reduce(
                (acc, setting) => ({ ...acc, [setting.key]: setting.value }),
                {}
              )),
        },
      });
    },
    [onSubmit, token]
  );

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      innerRef={formRef}
    >
      <Form>
        <SettingsArrayField name="settingsArray" />
      </Form>
    </Formik>
  );
};
